import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getProfile, updateProfile, changePassword, getSubscription, cancelSubscription } from '../api/profile';
import Button from '../components/Button';
import styles from './Profile.module.css';

// Опции пола для выпадающего списка
const GENDER_OPTIONS = [
  { value: '', label: 'Не указан' },
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' },
];

// Начальное состояние формы
const INITIAL_FORM = {
  last_name: '',
  first_name: '',
  patronymic: '',
  gender: '',
  date_of_birth: '',
  height_cm: '',
  weight_kg: '',
  phone: '',
  telegram_username: '',
};

// Начальное состояние формы смены пароля
const INITIAL_PASSWORD_FORM = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

// Коды стран (сортированы от длинного к короткому для жадного поиска)
const COUNTRY_DIAL_CODES = [
  { prefix: '+998', flag: '🇺🇿' }, // Узбекистан
  { prefix: '+996', flag: '🇰🇬' }, // Кыргызстан
  { prefix: '+995', flag: '🇬🇪' }, // Грузия
  { prefix: '+994', flag: '🇦🇿' }, // Азербайджан
  { prefix: '+993', flag: '🇹🇲' }, // Туркменистан
  { prefix: '+992', flag: '🇹🇯' }, // Таджикистан
  { prefix: '+380', flag: '🇺🇦' }, // Украина
  { prefix: '+375', flag: '🇧🇾' }, // Беларусь
  { prefix: '+374', flag: '🇦🇲' }, // Армения
  { prefix: '+971', flag: '🇦🇪' }, // ОАЭ
  { prefix: '+966', flag: '🇸🇦' }, // Саудовская Аравия
  { prefix: '+972', flag: '🇮🇱' }, // Израиль
  { prefix: '+886', flag: '🇹🇼' }, // Тайвань
  { prefix: '+852', flag: '🇭🇰' }, // Гонконг
  { prefix: '+389', flag: '🇲🇰' }, // Северная Македония
  { prefix: '+382', flag: '🇲🇪' }, // Черногория
  { prefix: '+381', flag: '🇷🇸' }, // Сербия
  { prefix: '+370', flag: '🇱🇹' }, // Литва
  { prefix: '+371', flag: '🇱🇻' }, // Латвия
  { prefix: '+372', flag: '🇪🇪' }, // Эстония
  { prefix: '+420', flag: '🇨🇿' }, // Чехия
  { prefix: '+421', flag: '🇸🇰' }, // Словакия
  { prefix: '+90', flag: '🇹🇷' },  // Турция
  { prefix: '+86', flag: '🇨🇳' },  // Китай
  { prefix: '+82', flag: '🇰🇷' },  // Южная Корея
  { prefix: '+81', flag: '🇯🇵' },  // Япония
  { prefix: '+66', flag: '🇹🇭' },  // Таиланд
  { prefix: '+65', flag: '🇸🇬' },  // Сингапур
  { prefix: '+62', flag: '🇮🇩' },  // Индонезия
  { prefix: '+61', flag: '🇦🇺' },  // Австралия
  { prefix: '+60', flag: '🇲🇾' },  // Малайзия
  { prefix: '+55', flag: '🇧🇷' },  // Бразилия
  { prefix: '+54', flag: '🇦🇷' },  // Аргентина
  { prefix: '+52', flag: '🇲🇽' },  // Мексика
  { prefix: '+49', flag: '🇩🇪' },  // Германия
  { prefix: '+48', flag: '🇵🇱' },  // Польша
  { prefix: '+47', flag: '🇳🇴' },  // Норвегия
  { prefix: '+46', flag: '🇸🇪' },  // Швеция
  { prefix: '+45', flag: '🇩🇰' },  // Дания
  { prefix: '+44', flag: '🇬🇧' },  // Великобритания
  { prefix: '+43', flag: '🇦🇹' },  // Австрия
  { prefix: '+41', flag: '🇨🇭' },  // Швейцария
  { prefix: '+40', flag: '🇷🇴' },  // Румыния
  { prefix: '+39', flag: '🇮🇹' },  // Италия
  { prefix: '+38', flag: '🇺🇦' },  // Украина (альт.)
  { prefix: '+37', flag: '🇱🇹' },  // Литва (альт.)
  { prefix: '+36', flag: '🇭🇺' },  // Венгрия
  { prefix: '+34', flag: '🇪🇸' },  // Испания
  { prefix: '+33', flag: '🇫🇷' },  // Франция
  { prefix: '+32', flag: '🇧🇪' },  // Бельгия
  { prefix: '+31', flag: '🇳🇱' },  // Нидерланды
  { prefix: '+30', flag: '🇬🇷' },  // Греция
  { prefix: '+20', flag: '🇪🇬' },  // Египет
  { prefix: '+91', flag: '🇮🇳' },  // Индия
  { prefix: '+92', flag: '🇵🇰' },  // Пакистан
  { prefix: '+93', flag: '🇦🇫' },  // Афганистан
  { prefix: '+98', flag: '🇮🇷' },  // Иран
  { prefix: '+7', flag: '🇷🇺' },   // Россия / Казахстан
  { prefix: '+1', flag: '🇺🇸' },   // США / Канада
];

// Определяет флаг страны по номеру телефона
const getCountryFlag = (phone) => {
  if (!phone || !phone.startsWith('+')) return null;
  for (const { prefix, flag } of COUNTRY_DIAL_CODES) {
    if (phone.startsWith(prefix)) return flag;
  }
  return null;
};

// Валидирует номер телефона: +, затем 7-15 цифр
const validatePhone = (phone) => {
  if (!phone) return true; // телефон опционален
  return /^\+[1-9]\d{6,14}$/.test(phone.replace(/\s/g, ''));
};

// Форматирует дату из ISO в формат input[type=date] (YYYY-MM-DD)
const formatDateForInput = (isoDate) => {
  if (!isoDate) return '';
  return new Date(isoDate).toISOString().split('T')[0];
};

const BOT_USERNAME = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'YourBotUsername';
const BOT_URL = `https://t.me/${BOT_USERNAME}`;

const Profile = () => {
  const { token, user: authUser, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const forcePasswordChange = authUser?.force_password_change;

  const [form, setForm] = useState(INITIAL_FORM);
  const [email, setEmail] = useState('');
  const [payUntil, setPayUntil] = useState(null);
  const [originalTelegram, setOriginalTelegram] = useState('');
  const [inChat, setInChat] = useState(false);
  const [telegramModal, setTelegramModal] = useState(false);
  const [botLinkCopied, setBotLinkCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Автопродление: null — не загружено / CloudPayments недоступен
  const [subscription, setSubscription] = useState(null);
  const [isCancellingSub, setIsCancellingSub] = useState(false);
  const [subMessage, setSubMessage] = useState({ text: '', type: '' });

  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Загружаем данные профиля при монтировании компонента
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile(token);
        setEmail(data.email);
        setPayUntil(data.pay_until || null);
        setInChat(data.in_chat || false);
        const tg = data.telegram_username || '';
        setOriginalTelegram(tg);
        setForm({
          last_name: data.last_name || '',
          first_name: data.first_name || '',
          patronymic: data.patronymic || '',
          gender: data.gender || '',
          date_of_birth: formatDateForInput(data.date_of_birth),
          height_cm: data.height_cm ?? '',
          weight_kg: data.weight_kg ?? '',
          phone: data.phone || '',
          telegram_username: tg.replace(/^@/, ''),
        });
      } catch (err) {
        setError('Не удалось загрузить данные профиля');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  // Статус автопродления грузим отдельно — CloudPayments может отвечать медленно
  useEffect(() => {
    getSubscription(token)
      .then(setSubscription)
      .catch(() => setSubscription(null));
  }, [token]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Отключить автопродление? Доступ к клубу сохранится до конца оплаченного периода.')) return;
    setIsCancellingSub(true);
    setSubMessage({ text: '', type: '' });
    try {
      const data = await cancelSubscription(token);
      setSubMessage({ text: data.message, type: 'success' });
      setSubscription({ auto_renew: false, subscriptions: [] });
    } catch (err) {
      setSubMessage({ text: err.response?.data?.message || 'Не удалось отключить автопродление', type: 'error' });
    } finally {
      setIsCancellingSub(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = name === 'telegram_username' ? value.replace(/^@/, '') : value;
    setForm((prev) => ({ ...prev, [name]: cleanValue }));
    if (name === 'phone') {
      setPhoneError(validatePhone(value) ? '' : 'Введите номер в формате +79001234567');
    }
    if (successMessage) setSuccessMessage('');
    if (error) setError('');
  };

  const doSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const tg = `@${form.telegram_username.trim()}`;
      await updateProfile(token, { ...form, telegram_username: tg });
      setOriginalTelegram(tg);
      if (tg !== originalTelegram) setInChat(false);
      updateUser({ telegram_username: tg });
      setSuccessMessage('Профиль успешно сохранён');
    } catch (err) {
      const message = err.response?.data?.message || 'Не удалось сохранить профиль';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tgNick = form.telegram_username.trim();
    if (!tgNick) {
      setError('Telegram-ник обязателен');
      return;
    }
    if (!/^[a-zA-Z0-9_]{5,32}$/.test(tgNick)) {
      setError('Telegram-ник должен содержать 5–32 символа: буквы, цифры, _');
      return;
    }
    if (!validatePhone(form.phone)) {
      setPhoneError('Введите номер в формате +79001234567');
      return;
    }
    // Если telegram-ник изменился и пользователь состоит в чате — показать предупреждение
    const newTg = `@${tgNick}`;
    if (newTg !== originalTelegram && inChat) {
      setTelegramModal(true);
      return;
    }
    await doSave();
  };

  const handleTelegramConfirm = async () => {
    setTelegramModal(false);
    await doSave();
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmPassword } = passwordForm;

    if (newPassword.length < 6) {
      setPasswordError('Новый пароль должен содержать не менее 6 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Новые пароли не совпадают');
      return;
    }

    setIsChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      await changePassword(token, oldPassword, newPassword);
      setPasswordSuccess('Пароль успешно изменён');
      setPasswordForm(INITIAL_PASSWORD_FORM);
      // Снимаем флаг принудительной смены пароля в контексте
      if (forcePasswordChange) {
        updateUser({ force_password_change: false });
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Не удалось изменить пароль';
      setPasswordError(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleCopyBotLink = async () => {
    try {
      await navigator.clipboard.writeText(BOT_URL);
      setBotLinkCopied(true);
      setTimeout(() => setBotLinkCopied(false), 2000);
    } catch {
      // fallback для браузеров без clipboard API
      window.open(BOT_URL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Ближайшее списание среди активных подписок
  const nextCharge = subscription?.subscriptions
    ?.map((sub) => sub.next_charge_at)
    .filter(Boolean)
    .sort()[0];

  if (isLoading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.spinner} />
        <p>Загрузка профиля...</p>
      </div>
    );
  }

  const countryFlag = getCountryFlag(form.phone);

  return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.layout}>
          {/* Боковая панель */}
          <aside className={styles.sidebar}>
            <div className={styles.avatarWrap}>
              <svg viewBox="0 0 80 80" fill="none" className={styles.avatarSvg}>
                <circle cx="40" cy="28" r="20" fill="rgba(42,157,143,0.2)" />
                <ellipse cx="40" cy="74" rx="32" ry="18" fill="rgba(42,157,143,0.12)" />
              </svg>
            </div>
            <p className={styles.sidebarEmail}>{email}</p>
            <p className={styles.sidebarLabel}>Участник клуба</p>
            {authUser?.is_admin && (
              <Link to="/admin" className={styles.adminLink}>
                Админ-панель
              </Link>
            )}
            <Button variant="outline" size="sm" onClick={handleLogout} className={styles.logoutButton}>
              Выйти из аккаунта
            </Button>
          </aside>

          {/* Основной контент */}
          <main className={styles.main}>
            <h1 className={styles.pageTitle}>Личный кабинет</h1>

            {/* Баннер обязательного заполнения Telegram */}
            {!authUser?.telegram_username && (
              <div className={styles.forceBanner}>
                Для продолжения работы укажите ваш Telegram-ник.
              </div>
            )}

            {/* Баннер принудительной смены пароля */}
            {forcePasswordChange && (
              <div className={styles.forceBanner}>
                ⚠ Администратор запросил смену пароля. Пожалуйста, задайте новый пароль ниже.
              </div>
            )}

            <p className={styles.pageSubtitle}>
              Заполните данные профиля, чтобы наши специалисты могли предоставить
              персонализированные рекомендации
            </p>

            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              {/* Блок: ФИО */}
              <fieldset className={styles.fieldset}>
                <legend className={styles.fieldsetTitle}>Личные данные</legend>
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="last_name" className={styles.label}>
                      Фамилия
                    </label>
                    <input
                      id="last_name"
                      type="text"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="Иванов"
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="first_name" className={styles.label}>
                      Имя
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="Иван"
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="patronymic" className={styles.label}>
                      Отчество
                    </label>
                    <input
                      id="patronymic"
                      type="text"
                      name="patronymic"
                      value={form.patronymic}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="Иванович"
                    />
                  </div>
                </div>

                {/* Telegram и телефон */}
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="telegram_username" className={styles.label}>
                      Telegram-ник
                    </label>
                    <input
                      id="telegram_username"
                      type="text"
                      name="telegram_username"
                      value={form.telegram_username}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="@username"
                      autoComplete="off"
                    />
                    {!inChat && (
                      <div
                        className={styles.botHint}
                        title="Бот не может писать первым. Нажмите, чтобы перейти к боту и отправить сообщение первым"
                      >
                        <span className={styles.botHintText}>
                          Напишите боту первым, чтобы получить доступ в чат
                        </span>
                        <div className={styles.botHintActions}>
                          <a
                            href={BOT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.botHintLink}
                          >
                            Перейти к боту
                          </a>
                          <button
                            type="button"
                            className={styles.botHintCopy}
                            onClick={handleCopyBotLink}
                          >
                            {botLinkCopied ? 'Скопировано!' : 'Скопировать ссылку'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Номер телефона */}
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="phone" className={styles.label}>
                      Номер телефона
                    </label>
                    <div className={styles.phoneWrap}>
                      {countryFlag && (
                        <span className={styles.phoneFlag} aria-hidden="true">
                          {countryFlag}
                        </span>
                      )}
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className={`${styles.input} ${styles.phoneInput} ${phoneError ? styles.inputError : ''}`}
                        placeholder="+79001234567"
                      />
                    </div>
                    {phoneError && <span className={styles.fieldError}>{phoneError}</span>}
                  </div>
                </div>
              </fieldset>

              {/* Блок: Биометрические данные */}
              <fieldset className={styles.fieldset}>
                <legend className={styles.fieldsetTitle}>Биометрические данные</legend>
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="gender" className={styles.label}>
                      Пол
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      value={form.gender}
                      onChange={handleChange}
                      className={styles.input}
                    >
                      {GENDER_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="date_of_birth" className={styles.label}>
                      Дата рождения
                    </label>
                    <input
                      id="date_of_birth"
                      type="date"
                      name="date_of_birth"
                      value={form.date_of_birth}
                      onChange={handleChange}
                      className={styles.input}
                    />
                  </div>
                </div>
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="height_cm" className={styles.label}>
                      Рост, см
                    </label>
                    <input
                      id="height_cm"
                      type="number"
                      name="height_cm"
                      value={form.height_cm}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="175"
                      min="50"
                      max="250"
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="weight_kg" className={styles.label}>
                      Вес, кг
                    </label>
                    <input
                      id="weight_kg"
                      type="number"
                      name="weight_kg"
                      value={form.weight_kg}
                      onChange={handleChange}
                      className={styles.input}
                      placeholder="70.5"
                      min="20"
                      max="300"
                      step="0.1"
                    />
                  </div>
                </div>
              </fieldset>

              {/* Сообщения об успехе / ошибке */}
              {successMessage && <p className={styles.success}>{successMessage}</p>}
              {error && <p className={styles.error}>{error}</p>}

              <div className={styles.formActions}>
                <Button type="submit" variant="primary" size="md" disabled={isSaving}>
                  {isSaving ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </div>
            </form>

            {/* Блок: Подписка */}
            <div className={styles.form}>
              <fieldset className={styles.fieldset}>
                <legend className={styles.fieldsetTitle}>Подписка</legend>
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label className={styles.label}>Оплачено до</label>
                    <input
                      type="text"
                      readOnly
                      value={payUntil ? new Date(payUntil).toLocaleDateString('ru-RU') : 'Не указано'}
                      className={`${styles.input} ${styles.inputReadonly}`}
                    />
                  </div>
                  {subscription && (
                    <div className={styles.field}>
                      <label className={styles.label}>Автопродление</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          subscription.auto_renew
                            ? `Включено${nextCharge ? ` · следующее списание ${new Date(nextCharge).toLocaleDateString('ru-RU')}` : ''}`
                            : 'Отключено'
                        }
                        className={`${styles.input} ${styles.inputReadonly}`}
                      />
                    </div>
                  )}
                </div>
                {subscription?.auto_renew && (
                  <div className={styles.formActions}>
                    <Button type="button" variant="outline" size="md" onClick={handleCancelSubscription} disabled={isCancellingSub}>
                      {isCancellingSub ? 'Отключение...' : 'Отключить автопродление'}
                    </Button>
                  </div>
                )}
                {subMessage.text && <p className={`${styles[subMessage.type]} ${styles.subMessage}`}>{subMessage.text}</p>}
              </fieldset>
            </div>

            {/* Блок: Смена пароля */}
            <form onSubmit={handlePasswordSubmit} className={styles.form} noValidate>
              <fieldset className={styles.fieldset}>
                <legend className={styles.fieldsetTitle}>Смена пароля</legend>
                {/* При принудительной смене пароля текущий пароль не требуется */}
                {!forcePasswordChange && (
                  <div className={styles.fieldsRow}>
                    <div className={styles.field}>
                      <label htmlFor="oldPassword" className={styles.label}>
                        Текущий пароль
                      </label>
                      <input
                        id="oldPassword"
                        type="password"
                        name="oldPassword"
                        value={passwordForm.oldPassword}
                        onChange={handlePasswordChange}
                        className={styles.input}
                        placeholder="Введите текущий пароль"
                        autoComplete="current-password"
                      />
                    </div>
                  </div>
                )}
                <div className={styles.fieldsRow}>
                  <div className={styles.field}>
                    <label htmlFor="newPassword" className={styles.label}>
                      Новый пароль
                    </label>
                    <input
                      id="newPassword"
                      type="password"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      className={styles.input}
                      placeholder="Не менее 6 символов"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="confirmPassword" className={styles.label}>
                      Повторите новый пароль
                    </label>
                    <input
                      id="confirmPassword"
                      type="password"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      className={styles.input}
                      placeholder="Повторите новый пароль"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                {passwordSuccess && <p className={styles.success}>{passwordSuccess}</p>}
                {passwordError && <p className={styles.error}>{passwordError}</p>}

                <div className={styles.formActions}>
                  <Button type="submit" variant="secondary" size="md" disabled={isChangingPassword}>
                    {isChangingPassword ? 'Сохранение...' : 'Изменить пароль'}
                  </Button>
                </div>
              </fieldset>
            </form>
          </main>
        </div>
      </div>
      {/* Модальное окно подтверждения смены Telegram-ника */}
      {telegramModal && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalDialog}>
            <p className={styles.modalText}>Вас выкинет из чата клуба</p>
            <div className={styles.modalActions}>
              <Button variant="outline" size="sm" onClick={() => setTelegramModal(false)}>
                Отмена
              </Button>
              <Button variant="primary" size="sm" onClick={handleTelegramConfirm}>
                Согласиться на изменение
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
